import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import './ContinueLearning.css';

const ContinueLearning = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const fetchInProgressCourses = async () => {
      try {
        setLoading(true);
        const userEmail = localStorage.getItem('userEmail');
        if (!userEmail) {
          setLoading(false);
          return;
        }

        const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
        const userId = userInfo.user_id;

        const [enrolledCourses, coursesProgress, coursesSummary] = await Promise.all([
          apiFetch(`${window._env_.API_BASE_URL}/users/${userId}/courses`),
          apiFetch(`${window._env_.API_BASE_URL}/users/${userId}/courses/progress`),
          apiFetch(`${window._env_.API_BASE_URL}/courses/summary`)
        ]);

        const progressMap = coursesProgress.reduce((acc, progress) => {
          acc[progress.course_id] = progress.progress_percent;
          return acc;
        }, {});

        const summaryMap = coursesSummary.reduce((acc, summary) => {
          acc[summary.course_id] = {
            category: summary.course_category,
            level: summary.course_level
          };
          return acc;
        }, {});

        const formattedCourses = enrolledCourses
          .map(course => {
            const progress = progressMap[course.course_id] || 0;
            const summary = summaryMap[course.course_id] || { category: 'General', level: 'Beginner' };
            return {
              id: course.course_id,
              title: course.course_title,
              track: summary.category,
              department: course.department_name,
              progress: progress,
              lastAccessed: 'Recently',
              icon: '📚'
            };
          })
          .filter(course => course.progress >= 0 && course.progress < 100);

        setCourses(formattedCourses);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInProgressCourses();
  }, []);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  if (loading) {
    return <div className="continue-learning">Loading...</div>;
  }

  if (error) {
    return <div className="continue-learning">Error: {error}</div>;
  }

  if (courses.length === 0) {
    return (
      <div className="continue-learning">
        <div className="cl-header">
          <h2>Continue Learning</h2>
        </div>
        <p>No courses enrolled.</p>
      </div>
    );
  }

  return (
    <div className="continue-learning">
      <div className="cl-header">
        <h2>Continue Learning</h2>
      </div>
      <div className="cl-courses-container">
        <button className="cl-scroll-button cl-scroll-left" onClick={scrollLeft}>
          &#8249;
        </button>
        <div className="cl-courses" ref={scrollContainerRef}>
          {courses.map(course => (
            <div className="cl-course-card" key={course.title}>
              <div className="cl-course-icon">{course.icon}</div>
              <div className="cl-course-info">
                <div className="cl-progress-bar">
                  <div style={{ width: `${course.progress}%` }}></div>
                </div>
                <p>{course.progress}% complete</p>
                <h3>{course.title}</h3>
                <p className="cl-track">{course.track}</p>
                <p className="cl-last-accessed">Last accessed: {course.lastAccessed}</p>
                <Link
                  to="/course-videos"
                  state={{ courseTitle: course.title, department: course.department, courseId: course.id }}
                  className="cl-continue-link"
                >
                  Continue
                </Link>
              </div>
            </div>
          ))}
        </div>
        <button className="cl-scroll-button cl-scroll-right" onClick={scrollRight}>
          &#8250;
        </button>
      </div>
    </div>
  );
};

export default ContinueLearning;